import React, { useEffect, useState } from "react";
import { useMutation } from "@apollo/client";
import { LOGIN } from "../gql/auth";
import * as jose from "jose";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CssBaseline,
  FormControlLabel,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import logo from "../static/PPlay_logo.png";
import { PRIMARY_ORANGE } from "../components/layout/constants";

const autofillStyles = {
  WebkitBoxShadow: "0 0 0 1000px transparent inset",
  WebkitTextFillColor: "#1a1a1a",
  caretColor: "#1a1a1a",
  transition: "background-color 5000s ease-in-out 0s",
};

const fieldInputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    bgcolor: "#fff",
    fontSize: "0.875rem",
    "& fieldset": { borderColor: "#e5e5e5" },
    "&:hover fieldset": { borderColor: "#d5d5d5" },
    "&.Mui-focused fieldset": { borderColor: PRIMARY_ORANGE },
  },
  "& .MuiOutlinedInput-input": {
    backgroundColor: "transparent",
    "&:-webkit-autofill": autofillStyles,
    "&:-webkit-autofill:hover": autofillStyles,
    "&:-webkit-autofill:focus": autofillStyles,
    "&:-webkit-autofill:active": autofillStyles,
  },
};

const FieldLabel = ({ children }) => (
  <Typography sx={{ mb: 0.5, fontSize: "0.8rem", fontWeight: 500, color: "#3d3d3d" }}>
    {children}
  </Typography>
);

const Login = () => {
  const [values, setValues] = useState({
    username: "",
    password: "",
    showPassword: false,
  });
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({
    username: "",
    password: "",
  });
  const [showAlert, setShowAlert] = useState({ message: "", isError: false });
  const navigate = useNavigate();

  useEffect(() => {
    const loggedUser = window.localStorage.getItem("loggedUser");
    if (loggedUser) {
      navigate("/overview", { replace: true });
      return;
    }

    const savedUsername = window.localStorage.getItem("loginRememberUsername");
    if (savedUsername) {
      setValues((prev) => ({ ...prev, username: savedUsername }));
      setRememberMe(true);
    }
  }, [navigate]);

  const [postLogin] = useMutation(LOGIN, {
    onError: () => {
      setShowAlert({ message: "Error on server", isError: true });
      setTimeout(() => setShowAlert({ message: "", isError: false }), 3000);
      setLoading(false);
    },
    onCompleted: (result) => {
      const usernameToRemember = values.username;
      setValues({ username: "", password: "", showPassword: false });
      setLoading(false);

      if (result.AdminLogIn.error) {
        setShowAlert({ message: result.AdminLogIn.message, isError: true });
        setTimeout(() => setShowAlert({ message: "", isError: false }), 3000);
        return;
      }

      const decodedToken = jose.decodeJwt(result.AdminLogIn.accessToken);
      if (decodedToken.exp * 1000 < Date.now()) {
        navigate("/login");
        return;
      }

      if (rememberMe) {
        window.localStorage.setItem("loginRememberUsername", usernameToRemember);
      } else {
        window.localStorage.removeItem("loginRememberUsername");
      }

      const data = JSON.stringify({
        token: result.AdminLogIn.accessToken,
        userID: decodedToken.user_id,
      });

      window.localStorage.setItem("loggedUser", data);
      navigate("/overview", { replace: true });
    },
  });

  const handleClickShowPassword = () => {
    setValues((prev) => ({ ...prev, showPassword: !prev.showPassword }));
  };

  const handleChange = (prop) => (event) => {
    setValues((prev) => ({ ...prev, [prop]: event.target.value }));
  };

  const handleClick = () => {
    setErrors({ username: "", password: "" });
    setLoading(true);

    const tempErrors = {};
    if (!values.username) tempErrors.username = "Name field is required.";
    if (!values.password) tempErrors.password = "Password field is required.";

    if (tempErrors.username || tempErrors.password) {
      setErrors(tempErrors);
      setLoading(false);
      return;
    }

    postLogin({
      variables: { username: values.username, password: values.password },
    });
  };

  return (
    <>
      <CssBaseline />
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "#f3f3f3",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: { xs: 0, md: 3 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            width: "100%",
            maxWidth: 820,
            minHeight: { xs: "100vh", md: "auto" },
            alignItems: "stretch",
            bgcolor: "#fff",
            borderRadius: { xs: 0, md: "12px" },
            overflow: "hidden",
            boxShadow: { xs: "none", md: "0 8px 32px rgba(0, 0, 0, 0.08)" },
          }}
        >
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              width: "50%",
              flexShrink: 0,
              bgcolor: "#111",
              lineHeight: 0,
            }}
          >
            <Box
              component="img"
              src={`${process.env.PUBLIC_URL}/login_image.png`}
              alt="PowerPlay"
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                objectPosition: "left center",
                display: "block",
              }}
            />
          </Box>

          <Box
            sx={{
              width: { xs: "100%", md: "50%" },
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              px: { xs: 3, sm: 4, md: 4 },
              py: { xs: 4, md: 3 },
            }}
          >
            <Box sx={{ textAlign: "center", mb: 2.5 }}>
              <Box
                component="img"
                src={logo}
                alt="PowerPlay"
                sx={{ height: 32, mb: 2, objectFit: "contain" }}
              />
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, color: "#1a1a1a", mb: 0.5, fontSize: "1.15rem" }}
              >
                Welcome Back!
              </Typography>
              <Typography variant="body2" sx={{ color: "#888", fontSize: "0.8rem" }}>
                Sign in to your PowerPlay admin account.
              </Typography>
            </Box>

            <Box sx={{ maxWidth: 320, mx: "auto", width: "100%" }}>
              <Box sx={{ mb: 1.5 }}>
                <FieldLabel>Name</FieldLabel>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Enter your name"
                  value={values.username}
                  onChange={handleChange("username")}
                  error={Boolean(errors.username)}
                  helperText={errors.username}
                  sx={fieldInputSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonOutlineIcon sx={{ color: "#9e9e9e", fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <Box sx={{ mb: 1.5 }}>
                <FieldLabel>Password</FieldLabel>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Enter your password"
                  type={values.showPassword ? "text" : "password"}
                  value={values.password}
                  onChange={handleChange("password")}
                  error={Boolean(errors.password)}
                  helperText={errors.password}
                  sx={fieldInputSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlinedIcon sx={{ color: "#9e9e9e", fontSize: 20 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          edge="end"
                          size="small"
                        >
                          {values.showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2,
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      size="small"
                      sx={{
                        color: "#bbb",
                        "&.Mui-checked": { color: PRIMARY_ORANGE },
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ color: "#666" }}>
                      Remember me
                    </Typography>
                  }
                />
                {/* <Button
                  variant="text"
                  sx={{
                    textTransform: "none",
                    color: PRIMARY_ORANGE,
                    fontWeight: 500,
                    fontSize: "0.875rem",
                    minWidth: 0,
                    p: 0,
                  }}
                >
                  Forgot password?
                </Button> */}
              </Box>

              <LoadingButton
                fullWidth
                onClick={handleClick}
                loading={loading}
                variant="contained"
                sx={{
                  height: 42,
                  bgcolor: PRIMARY_ORANGE,
                  textTransform: "uppercase",
                  fontWeight: 700,
                  letterSpacing: "0.04em",
                  fontSize: "0.85rem",
                  borderRadius: "8px",
                  boxShadow: "none",
                  "&:hover": { bgcolor: "#f4511e", boxShadow: "none" },
                }}
              >
                Sign In
              </LoadingButton>
            </Box>
          </Box>
        </Box>
      </Box>

      {showAlert.message && !showAlert.isError && (
        <Alert sx={{ position: "fixed", bottom: "1em", right: "1em" }} severity="success">
          {showAlert.message}
        </Alert>
      )}
      {showAlert.message && showAlert.isError && (
        <Alert sx={{ position: "fixed", bottom: "1em", right: "1em" }} severity="warning">
          {showAlert.message}
        </Alert>
      )}
    </>
  );
};

export default Login;
